import type { ExpressImportResult, ExpressToolEntry } from '@/api'

/**
 * Importing a list of Express tools from a file on the Staff page. A list of a hundred tools with their logos is
 * larger than one request may be, so it is sent in parts. That is safe because an import changes only what differs:
 * a part sent twice, or a whole list imported again after a failure, changes nothing more.
 */

/** A part's size in characters of JSON, well inside what the API accepts in one request. */
export const IMPORT_PART_CHARS = 60_000
/** The most tools the API reads in one request. */
export const IMPORT_PART_ENTRIES = 100

export type ImportSend = (body: {
    tools: ExpressToolEntry[]
    dryRun?: boolean
    publish?: boolean
}) => Promise<ExpressImportResult>

/** The tools a file holds: a bare list, or `{ "tools": [...] }` as the export writes it. Throws when it is neither. */
export function entriesOfFile(text: string): ExpressToolEntry[] {
    let parsed: unknown
    try {
        parsed = JSON.parse(text)
    } catch (cause) {
        throw new Error('express_import_file_invalid', { cause })
    }
    const list = Array.isArray(parsed) ? parsed : (parsed as { tools?: unknown } | null)?.tools
    if (!Array.isArray(list) || list.length === 0 || list.some(item => !item || typeof item !== 'object')) {
        throw new Error('express_import_file_invalid')
    }
    return list as ExpressToolEntry[]
}

/** The list cut into parts small enough to send, in order. A tool too large for a part goes alone. */
export function partsOf(entries: readonly ExpressToolEntry[], maxChars = IMPORT_PART_CHARS): ExpressToolEntry[][] {
    const parts: ExpressToolEntry[][] = []
    let part: ExpressToolEntry[] = []
    let size = 0
    for (const entry of entries) {
        const chars = JSON.stringify(entry).length
        if (part.length > 0 && (size + chars > maxChars || part.length >= IMPORT_PART_ENTRIES)) {
            parts.push(part)
            part = []
            size = 0
        }
        part.push(entry)
        size += chars
    }
    if (part.length > 0) {
        parts.push(part)
    }
    return parts
}

/** Several parts' answers as one. Something was applied only if every part was. */
export function mergeResults(results: readonly ExpressImportResult[]): ExpressImportResult {
    const sum = (pick: (result: ExpressImportResult) => number): number =>
        results.reduce((total, result) => total + pick(result), 0)
    return {
        dryRun: results.every(result => result.dryRun),
        applied: results.length > 0 && results.every(result => result.applied),
        created: sum(result => result.created),
        updated: sum(result => result.updated),
        unchanged: sum(result => result.unchanged),
        refused: sum(result => result.refused),
        entries: results.flatMap(result => result.entries),
    }
}

/** Sends a list part by part, in order, and answers for the whole of it. */
export async function importAll(
    send: ImportSend,
    entries: readonly ExpressToolEntry[],
    options: { dryRun: boolean; publish?: boolean },
): Promise<ExpressImportResult> {
    const results: ExpressImportResult[] = []
    for (const tools of partsOf(entries)) {
        results.push(await send({ tools, dryRun: options.dryRun, ...(options.publish ? { publish: true } : {}) }))
    }
    return mergeResults(results)
}

/** The same slug twice in one file. The API refuses that within a part; across parts only the file can see it. */
export function repeatedSlugs(entries: readonly ExpressToolEntry[]): string[] {
    const seen = new Set<string>()
    const repeated = new Set<string>()
    for (const entry of entries) {
        const slug = String(entry.slug ?? '').toLowerCase()
        if (seen.has(slug)) {
            repeated.add(slug)
        }
        seen.add(slug)
    }
    return [...repeated]
}
